import PopoverMenu from '@deltek/specification-client/core/Navigation/PopoverMenu'
import { IconButton, MenuItem, Typography } from '@material-ui/core'
import { MoreVert } from '@material-ui/icons'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import SyncIcon from '@material-ui/icons/Sync'
import UpdateIcon from '@material-ui/icons/Update'
import { requestBPRemoveConfirmation } from 'api/graphQL/reactiveVars/workspaceToggles'
import React, { useCallback, useRef, useState } from 'react'
import { useBestPracticeTipCardStyles } from '../styles/useBestPracticeTipCardStyles'
import {
    BestPracticeTip,
    BPTipAction,
    BPTipParameters,
    parameterConfig,
} from '../types/bpManagement.types'

interface BestPracticeTipCardProps {
    tip: BestPracticeTip
    onClick: () => void
    isActive?: boolean
    onDelete?: () => void | Promise<void>
}

const ACTION_LABELS: Record<BPTipAction, string> = {
    SET: 'BEST PRACTICE ASSIGNED',
    MODIFY: 'BEST PRACTICE MODIFIED',
    REMOVE: 'BEST PRACTICE REMOVED',
    RESYNC: 'BEST PRACTICE RESYNCED',
}

const ACTION_ICONS: Record<BPTipAction, React.ElementType> = {
    SET: CheckCircleOutlineIcon,
    MODIFY: UpdateIcon,
    REMOVE: HighlightOffIcon,
    RESYNC: SyncIcon,
}

const formatTimestamp = (iso: string): string => {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const today = new Date()
    const sameDay =
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate()
    const timeText = d.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
    })
    if (sameDay) return `Today ${timeText}`
    const dateText = d.toLocaleDateString(undefined, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    })
    return `${dateText} ${timeText}`
}

const computeInitials = (name: string): string =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('')

const buildCriteriaPairs = (
    parameters: BPTipParameters | undefined
): { label: string; value: string }[] => {
    if (!parameters) return []
    return parameterConfig
        .map(({ key, label }) => ({
            label,
            value: parameters[key] ?? '',
        }))
        .filter((p) => p.value.length > 0)
}

export const BestPracticeTipCard = ({
    tip,
    onClick,
    isActive,
    onDelete,
}: BestPracticeTipCardProps) => {
    const classes = useBestPracticeTipCardStyles()
    const [isActionPopoverDisplayed, setIsActionPopoverDisplayed] = useState(false)
    const actionRef = useRef<HTMLButtonElement>(null)

    const handleActionPopoverClick = useCallback(() => {
        setIsActionPopoverDisplayed((prev) => !prev)
    }, [])

    const handleDeleteBestPractice = useCallback(async () => {
        setIsActionPopoverDisplayed(false)
        const confirmed = await requestBPRemoveConfirmation()
        if (confirmed) onDelete?.()
    }, [onDelete])

    const {
        action,
        type,
        author,
        occurredAt,
        parameters,
        changeDescription,
    } = tip

    const initials = computeInitials(author.name)
    const timestamp = formatTimestamp(occurredAt)
    const isParameter = type === 'parameter'
    const criteriaPairs = isParameter ? buildCriteriaPairs(parameters) : []
    const actionLabel = ACTION_LABELS[action]
    const ActionIcon = ACTION_ICONS[action]
    const showCriteria = isParameter && criteriaPairs.length > 0

    return (
        <div
            className={`${classes.card}${
                isActive ? ` ${classes.cardActive}` : ''
            }${action === 'REMOVE' ? ` ${classes.cardRemoved}` : ''}`}
            data-testid="bestPracticeTipCard"
            onClick={action !== 'REMOVE' ? onClick : undefined}
            role={action !== 'REMOVE' ? 'button' : undefined}
            tabIndex={action !== 'REMOVE' ? 0 : undefined}
            onKeyDown={
                action !== 'REMOVE'
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') onClick()
                      }
                    : undefined
            }
        >
            <div className={classes.avatar} aria-hidden>
                {initials}
            </div>
            <div className={classes.body}>
                <div className={classes.headerRow}>
                    <Typography className={classes.userName}>
                        {author.name}
                    </Typography>
                    <Typography className={classes.timestamp}>
                        {timestamp}
                    </Typography>
                </div>
                <Typography className={classes.typeLabel}>
                    {showCriteria ? `${actionLabel}:` : actionLabel}
                </Typography>
                {showCriteria && (
                    <Typography className={classes.criteria}>
                        {criteriaPairs.map((p, idx) => (
                            <React.Fragment key={p.label}>
                                {idx > 0 && '   '}
                                <span className={classes.criteriaLabel}>
                                    {p.label}:
                                </span>{' '}
                                {p.value}
                            </React.Fragment>
                        ))}
                    </Typography>
                )}
                {action === 'MODIFY' && changeDescription && (
                    <Typography className={classes.criteria}>
                        {changeDescription}
                    </Typography>
                )}
            </div>
            <div className={classes.rightActions}>
                <ActionIcon className={classes.actionIcon} aria-hidden />
                {action !== 'REMOVE' && (
                    <div onClick={(e) => e.stopPropagation()}>
                        <IconButton
                            className={classes.moreIconBeta}
                            data-testid="moreActionButtonBeta"
                            title="More actions"
                            aria-haspopup="true"
                            aria-label="More actions button"
                            aria-expanded={isActionPopoverDisplayed}
                            onClick={handleActionPopoverClick}
                            ref={actionRef}
                            disableRipple
                            disableFocusRipple
                        >
                            <MoreVert fontSize="small" />
                        </IconButton>
                        <PopoverMenu
                            open={isActionPopoverDisplayed}
                            anchorEl={actionRef.current}
                            onClose={handleActionPopoverClick}
                        >
                            <MenuItem onClick={handleDeleteBestPractice}>
                                Delete Best Practice
                            </MenuItem>
                        </PopoverMenu>
                    </div>
                )}
            </div>
        </div>
    )
}
